import React from 'react';
import { BadgeCheck } from 'lucide-react';
import type { RfqHubFileRelease, RfqPackage, RfqProject, RfqSubmission, VendorFileRelease } from '../../types';
import { formatCncQuoteSummary, packageBoardFeet } from '../../utils/cncVendorExport';
import {
  cncSubmissionHistoryKey,
  groupQuoteSubmissionHistory,
} from '../../utils/rfqSubmissionHistory';
import { timberQuoteStructureScopeLabel } from '../../utils/timberQuoteStreamKey';
import { formatQuoteTotal, submissionQuoteTotal } from '../../utils/rfqQuoteTotal';
import { normalizeWorkType } from '../../utils/vendorHelpers';
import {
  approvedSubmissionIdsForPackage,
  cncAwardedSubmissionIdsForPackage,
  cncPackageApprovedBadgeLabel,
  timberSubmissionApprovedBadgeLabel,
} from '../../utils/vendorPortalApprovals';
import { isRfqQuoteRejected } from '../../utils/rfqQuoteRejectionTargets';
import {
  PENDING_REVISION_HINT,
  resolveQuoteStreamDisplay,
  visibleQuoteRowsNewestFirst,
} from '../../utils/vendorPortalQuoteDisplay';
import {
  buildVendorPortalTimberQuoteHeadings,
  isActiveTimberQuoteSubmission,
} from '../../utils/vendorPortalTimberQuotes';
import {
  CNC_QUOTE_PLACEHOLDER_ON_FILE_LABEL,
  isQuotePlaceholderPackage,
  isQuotePlaceholderSubmission,
} from '../../utils/combinedQuoteSplit';
import {
  shouldDeemphasizeRfqVersion,
  vendorPackageQuoteStatusLabel,
  vendorPackageVersionLabelForPackage,
  vendorPackageVersionName,
  type VendorPackageVersionRow,
} from '../../utils/vendorPackageVersion';

export type VendorPortalVersionQuoteLine = {
  sub: RfqSubmission;
  priceLabel: string;
  species: string | null;
  drynessLabel: string | null;
  structureScopeLabel: string | null;
  isPendingRevision: boolean;
  previousCount: number;
};

export type VendorPortalVersionHistoryRow = {
  row: VendorPackageVersionRow;
  versionName: string | undefined;
  rowIsCnc: boolean;
  rowApprovedIds: Set<string>;
  versionQuoteLines: VendorPortalVersionQuoteLine[];
  versionInactive: boolean;
  notBiddingOnly: boolean;
};

export interface VendorPortalQuotesByVersionPanelProps {
  rows: VendorPortalVersionHistoryRow[];
  project: RfqProject;
  orgId: string | undefined;
  onViewSubmission: (sub: RfqSubmission) => void;
  canSeeFinancials?: boolean;
}

const VendorPortalQuotesByVersionPanel: React.FC<VendorPortalQuotesByVersionPanelProps> = ({
  rows,
  project,
  orgId,
  onViewSubmission,
  canSeeFinancials = true,
}) => (
  <ul className="vp-version-history-list">
    {rows.map(
      ({
        row,
        versionName,
        rowIsCnc,
        rowApprovedIds,
        versionQuoteLines,
        versionInactive,
        notBiddingOnly,
      }) => (
        <li
          key={row.pkg.id}
          className={`vp-version-history-version${
            versionInactive ? ' vp-version-history-version--inactive' : ''
          }`}
        >
          <span className="vp-version-history-label">
            {vendorPackageVersionLabelForPackage(row.pkg)}
            {versionName && <span className="vp-version-history-name">{versionName}</span>}
          </span>
          {versionQuoteLines.length === 0 ? (
            <div className="vp-version-history-quote-line">
              {notBiddingOnly ? (
                <span>Not bidding</span>
              ) : (
                <span className="vp-version-history-empty">
                  {vendorPackageQuoteStatusLabel('none')}
                </span>
              )}
            </div>
          ) : (
            <ul className="vp-version-history-quotes">
              {versionQuoteLines.map(
                ({
                  sub,
                  priceLabel,
                  species,
                  drynessLabel,
                  structureScopeLabel,
                  isPendingRevision,
                  previousCount,
                }) => (
                  <li key={sub.id} className="vp-version-history-quote-line">
                    {!!sub.id && rowApprovedIds.has(sub.id) && (
                      <span className="vp-quote-badge vp-quote-badge--approved">
                        <BadgeCheck size={11} aria-hidden />{' '}
                        {rowIsCnc
                          ? cncPackageApprovedBadgeLabel(project, row.pkg, orgId)
                          : timberSubmissionApprovedBadgeLabel(project, sub.id)}
                      </span>
                    )}
                    {!(!!sub.id && rowApprovedIds.has(sub.id))
                      && isRfqQuoteRejected(sub) && (
                      <span className="vp-quote-badge vp-quote-badge--rejected">Rejected</span>
                    )}
                    {isPendingRevision && (
                      <span className="vp-quote-badge vp-quote-badge--pending-revision">
                        {PENDING_REVISION_HINT}
                      </span>
                    )}
                    {(() => {
                      const hideCncCarveOut =
                        rowIsCnc
                        && (isQuotePlaceholderPackage(row.pkg) || isQuotePlaceholderSubmission(sub));
                      const cncSummary =
                        canSeeFinancials && rowIsCnc && !hideCncCarveOut && sub.cncQuote
                          ? formatCncQuoteSummary(sub.cncQuote, packageBoardFeet(row.pkg))
                          : '';
                      return (
                        <>
                          {canSeeFinancials
                            && !hideCncCarveOut
                            && priceLabel
                            && priceLabel !== '—'
                            && <span>{priceLabel}</span>}
                          {hideCncCarveOut && (
                            <span>{CNC_QUOTE_PLACEHOLDER_ON_FILE_LABEL}</span>
                          )}
                          {species && (
                            <span className="vp-quote-badge vp-quote-badge--species">{species}</span>
                          )}
                          {structureScopeLabel && (
                            <span className="vp-quote-badge">{structureScopeLabel}</span>
                          )}
                          {drynessLabel && (
                            <span className="vp-quote-badge vp-quote-badge--dryness">{drynessLabel}</span>
                          )}
                          {cncSummary ? <span>· {cncSummary}</span> : null}
                          {!rowIsCnc && sub.fileName && <span>· {sub.fileName}</span>}
                          {rowIsCnc && !hideCncCarveOut && !sub.cncQuote && sub.fileName && (
                            <span>· {sub.fileName}</span>
                          )}
                          {canSeeFinancials && sub.storagePath && (
                            <>
                              {' · '}
                              <button
                                type="button"
                                className="vp-link-btn"
                                onClick={() => void onViewSubmission(sub)}
                              >
                                View quote PDF
                              </button>
                            </>
                          )}
                        </>
                      );
                    })()}
                    {sub.createdAt instanceof Date && (
                      <span className="vp-muted">— {sub.createdAt.toLocaleString()}</span>
                    )}
                    {previousCount > 0 && (
                      <span className="vp-muted">
                        · {previousCount} earlier {previousCount === 1 ? 'revision' : 'revisions'}
                      </span>
                    )}
                  </li>
                ),
              )}
            </ul>
          )}
        </li>
      ),
    )}
  </ul>
);

export function buildVendorPortalVersionHistoryRows(
  versions: VendorPackageVersionRow[],
  project: RfqProject,
  packages: RfqPackage[],
  submissionsByPackage: Map<string, RfqSubmission[]>,
  orgId: string | undefined,
  versionNameContext: {
    hubReleaseById?: Map<string, RfqHubFileRelease>;
    vendorReleases: VendorFileRelease[];
    orgId: string | undefined;
  },
): VendorPortalVersionHistoryRow[] {
  return versions.map(row => {
    const rowSubs = row.submissions;
    const versionName = vendorPackageVersionName(row.pkg, versionNameContext);
    const rowIsCnc = normalizeWorkType(row.pkg.workType) === 'cnc_quote';
    const rowApprovedIds = rowIsCnc
      ? cncAwardedSubmissionIdsForPackage(project, row.pkg, orgId, rowSubs)
      : approvedSubmissionIdsForPackage(project, row.pkg, packages, submissionsByPackage);
    const timberHeadings =
      !rowIsCnc && orgId
        ? buildVendorPortalTimberQuoteHeadings(rowSubs, project, orgId, rowApprovedIds)
        : [];
    const cncGroups = rowIsCnc
      ? groupQuoteSubmissionHistory(
          rowSubs.filter(isActiveTimberQuoteSubmission),
          cncSubmissionHistoryKey,
        )
      : [];
    const versionQuoteLines = (rowIsCnc
      ? cncGroups.flatMap(group => {
          const display = resolveQuoteStreamDisplay(group, rowApprovedIds);
          return visibleQuoteRowsNewestFirst(display, rowApprovedIds).map(line => ({
            sub: line.sub,
            priceLabel: formatQuoteTotal(submissionQuoteTotal(line.sub)),
            species: null as string | null,
            drynessLabel: null as string | null,
            structureScopeLabel: timberQuoteStructureScopeLabel(line.sub),
            isPendingRevision: line.isPendingRevision,
            previousCount: line.showPreviousHistory ? line.previous.length : 0,
          }));
        })
      : timberHeadings
          .filter(h => h.submission.storagePath)
          .flatMap(h =>
            visibleQuoteRowsNewestFirst(
              {
                primary: h.submission,
                pendingRevisions: h.pendingRevisions,
                previous: h.previous,
                hasApprovedPrimary:
                  h.pendingRevisions.length > 0
                  || (!!h.submission.id && rowApprovedIds.has(h.submission.id)),
              },
              rowApprovedIds,
            ).map(line => ({
              sub: line.sub,
              priceLabel:
                line.sub === h.submission
                  ? h.priceLabel
                  : formatQuoteTotal(submissionQuoteTotal(line.sub)),
              species: h.species,
              drynessLabel: h.drynessLabel,
              structureScopeLabel: h.structureScopeLabel,
              isPendingRevision: line.isPendingRevision,
              previousCount: line.showPreviousHistory ? line.previous.length : 0,
            })),
          )
    ).sort((a, b) => {
      const ta = a.sub.createdAt instanceof Date ? a.sub.createdAt.getTime() : 0;
      const tb = b.sub.createdAt instanceof Date ? b.sub.createdAt.getTime() : 0;
      return tb - ta;
    });
    const versionInactive = shouldDeemphasizeRfqVersion(row, versions);
    const notBiddingOnly = rowSubs.some(s => s.notBidding) && versionQuoteLines.length === 0;

    return {
      row,
      versionName,
      rowIsCnc,
      rowApprovedIds,
      versionQuoteLines,
      versionInactive,
      notBiddingOnly,
    };
  });
}

export default VendorPortalQuotesByVersionPanel;
